import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Alert,
  Button,
  TextField,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import Login from '@mui/icons-material/Login';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import ConfirmationNumberOutlined from '@mui/icons-material/ConfirmationNumberOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { apiService, API_ENDPOINTS } from '../../config/api';
import { useSupportForm } from '../../hooks/useSupportForm';
import CategorySelector from './CategorySelector';
import SupportForm from './SupportForm';
import SupportTicketHistory from './SupportTicketHistory';
import { SUPPORT_EMAIL } from '../../services/supportService';

interface SupportPanelProps {
  onClose: () => void;
  fabRef: React.RefObject<HTMLButtonElement | null>;
  initialTicketId?: string | null;
}

const MotionPaper = motion.create(Paper);

const SupportPanel: React.FC<SupportPanelProps> = ({ onClose, fabRef, initialTicketId = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const shouldReduceMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const { user } = useSecureAuth();

  const {
    formData,
    setField,
    resetCategory,
    isSubmitting,
    submitStatus,
    error,
    submitForm,
    hasDraft,
    restoreDraft,
    dismissDraft,
    resetForm,
    browserInfo,
    ticket,
    addScreenshots,
    removeScreenshot,
  } = useSupportForm();

  const [studies, setStudies] = useState<Array<{ id: string; name: string }>>([]);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const [view, setView] = useState<'new' | 'history'>(initialTicketId ? 'history' : 'new');
  const [historyTicketId, setHistoryTicketId] = useState<string | null>(initialTicketId);
  const newTicket = () => {
    if (ticket) resetForm();
    setCopied(false); setCopyError(false); setView('new');
  };

  // Fetch researcher's studies for the study selector
  useEffect(() => {
    if (!user) return;
    let active = true;
    apiService
      .get(API_ENDPOINTS.AUTH.RESEARCHER_STUDIES)
      .then((res: any) => {
        const list = res?.studies || res?.data || res || [];
        if (active && Array.isArray(list)) {
          setStudies(list.filter((s: any) => typeof s?.id === 'string' && typeof s?.name === 'string')
            .map((s: any) => ({ id: s.id, name: s.name })));
        }
      })
      .catch(() => {
        // non-critical
      });
    return () => { active = false; };
  }, [user]);

  // Focus management — focus panel on open
  useEffect(() => {
    const timer = setTimeout(() => {
      panelRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Return focus to FAB on close
  const handleClose = () => {
    if (isSubmitting) return;
    if (ticket) resetForm();
    onClose();
    // Allow exit animation, then focus FAB
    setTimeout(() => {
      fabRef.current?.focus();
    }, 250);
  };

  const researcherEmail = user?.email || '';

  return (
    <MotionPaper
      ref={panelRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="false"
      aria-labelledby="support-panel-title"
      onKeyDown={event => {
        if (event.key === 'Escape' && !event.defaultPrevented) {
          event.stopPropagation();
          handleClose();
        }
      }}
      elevation={8}
      initial={
        shouldReduceMotion
          ? { opacity: 0 }
          : { opacity: 0, y: 40, scale: 0.95 }
      }
      animate={
        shouldReduceMotion
          ? { opacity: 1 }
          : { opacity: 1, y: 0, scale: 1 }
      }
      exit={
        shouldReduceMotion
          ? { opacity: 0 }
          : { opacity: 0, y: 20 }
      }
      transition={
        shouldReduceMotion
          ? { duration: 0.15 }
          : submitStatus === 'success'
            ? { duration: 0.2, ease: 'easeOut' }
            : { type: 'spring', damping: 25, stiffness: 300 }
      }
      sx={{
        position: 'fixed',
        zIndex: theme.zIndex.modal + 1,
        outline: 'none',
        borderRadius: isMobile ? '16px 16px 0 0' : '16px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...(isMobile
          ? {
              bottom: 0,
              left: 0,
              right: 0,
              width: '100%',
              maxHeight: '85dvh',
            }
          : {
              bottom: 96,
              right: 24,
              width: 500,
              maxWidth: 'calc(100vw - 48px)',
              maxHeight: 'calc(100dvh - 120px)',
            }),
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Typography
          id="support-panel-title"
          variant="subtitle1"
          sx={{ fontWeight: 600 }}
        >
          How can we help?
        </Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={isSubmitting}
          aria-label="Close support panel"
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {user && <Tabs value={view} variant="fullWidth" aria-label="Support"
        onChange={(_event, value: 'new' | 'history') => {
          if (value === 'new') newTicket();
          else { setHistoryTicketId(null); setView('history'); }
        }} sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Tab id="support-new-tab" aria-controls="support-new-panel" value="new" label="New ticket" disabled={isSubmitting} icon={<AddCircleOutline fontSize="small" />} iconPosition="start" />
        <Tab id="support-history-tab" aria-controls="support-history-panel" value="history" label="My tickets" disabled={isSubmitting} icon={<ConfirmationNumberOutlined fontSize="small" />} iconPosition="start" />
      </Tabs>}

      {/* Body — scrollable */}
      <Box
        role={user ? 'tabpanel' : undefined}
        id={view === 'new' ? 'support-new-panel' : 'support-history-panel'}
        aria-labelledby={user ? view === 'new' ? 'support-new-tab' : 'support-history-tab' : undefined}
        sx={{
          overflowY: 'auto',
          px: 2,
          py: 2,
          flexGrow: 1,
        }}
      >
        {view === 'new' && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Submit a ticket to the StudySync team at {SUPPORT_EMAIL}.
        </Typography>}
        {!user && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2">Sign in to send a ticket or read replies.</Typography>
          <Button component={RouterLink} to={initialTicketId ? `/researcher/auth?redirect=${encodeURIComponent(`/?supportTicket=${initialTicketId}`)}` : '/researcher/auth'} variant="contained" fullWidth startIcon={<Login />} onClick={handleClose}>
            Log in before sending a support ticket
          </Button>
        </Box>}
        {/* Draft restore banner */}
        {user && view === 'new' && !ticket && hasDraft && !formData.category && (
          <Alert
            severity="info"
            sx={{ mb: 2, py: 0.5 }}
            action={
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button color="inherit" size="small" onClick={restoreDraft}>
                  Restore
                </Button>
                <Button color="inherit" size="small" onClick={dismissDraft}>
                  Discard
                </Button>
              </Box>
            }
          >
            <Typography variant="caption">You have a saved draft</Typography>
          </Alert>
        )}

        {/* Layer 1: Category selector (no category selected yet) */}
        {/* Layer 2: Form with category-specific fields */}
        {user && (view === 'history' ? <SupportTicketHistory onNewTicket={newTicket} initialTicketId={historyTicketId} /> : ticket ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="success">Your ticket is saved. Status: Open.</Alert>
            <TextField label="Ticket number" value={ticket.id} size="small" fullWidth InputProps={{ readOnly: true }}
              onFocus={event => event.target.select()} />
            <Button variant="outlined" onClick={async () => {
              try { await navigator.clipboard.writeText(ticket.id); setCopied(true); setCopyError(false); }
              catch { setCopyError(true); }
            }}>{copied ? 'Copied' : 'Copy ticket number'}</Button>
            {copyError && <Typography variant="body2">Select the ticket number above to copy it.</Typography>}
            {ticket.notificationStatus === 'accepted' ? (
              <Typography variant="body2">Read replies in My tickets. We'll also notify {researcherEmail} by email.</Typography>
            ) : (
              <Alert severity="warning">Your ticket is saved, but the email notification to our team hasn't been confirmed. You don't need to submit it again. Keep your ticket number.</Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button onClick={() => { setHistoryTicketId(ticket.id); setView('history'); }}>View ticket</Button>
              <Button onClick={() => { resetForm(); setCopied(false); setCopyError(false); }}>New ticket</Button>
              <Button variant="contained" onClick={handleClose}>Done</Button>
            </Box>
          </Box>
        ) : !formData.category ? (
          <CategorySelector
            selected={formData.category}
            onSelect={(cat) => setField('category', cat)}
          />
        ) : (
          <SupportForm
            formData={formData}
            setField={setField}
            onBack={resetCategory}
            onSubmit={submitForm}
            isSubmitting={isSubmitting}
            error={error}
            researcherEmail={researcherEmail}
            browserInfo={browserInfo}
            addScreenshots={addScreenshots}
            removeScreenshot={removeScreenshot}
            studies={studies}
          />
        ))}
      </Box>
    </MotionPaper>
  );
};

export default SupportPanel;
